<template>
  <div
    class="header"
    @mouseenter="nextMessage"
  >
    <FlexLayout
      align="start"
      gap="md"
    >
      <FlexItem
        class="logo"
        width="4rem"
      >
        <ToolTip
          :message="shuffledMessages[currentNumber]"
          notch="right"
        >
          <AppLogo title="CHAPPIE" />
        </ToolTip>
      </FlexItem>

      <FlexItem grow>
        <BaseGroup>
          <FormControl
            v-model="search"
            class="search-input"
            flush
            placeholder="Search"
            type="text"
            @keyup.enter="handleSearch"
          />
          <BaseButton
            color="primary"
            icon="magnifying-glass"
            @click="handleSearch"
          />
          <BaseButton
            color="secondary"
            icon="x"
            @click="clearSearch"
          />
        </BaseGroup>
      </FlexItem>

      <FlexItem v-if="isAuthenticated && user">
        <FlexLayout
          align="center"
          gap="sm"
        >
          <FlexItem>
            <BaseIcon
              id="smiley"
              inline
            />
          </FlexItem>
          <FlexItem>
            {{ userDisplayName }}
          </FlexItem>
          <FlexItem>
            <LogoutButton />
          </FlexItem>
        </FlexLayout>
      </FlexItem>
    </FlexLayout>
  </div>
</template>

<script setup lang="ts">
import { usePlansQueryStore } from '@stores/plansQuery';
import { useSessionStore } from '@stores/session';
import { useRoute, useRouter } from 'vue-router';

import AppLogo from './AppLogo.vue';
import LogoutButton from './LogoutButton.vue';

const sessionStore = useSessionStore();
const plansQueryStore = usePlansQueryStore();
const route = useRoute();
const router = useRouter();

const search = ref('');
const currentNumber = ref(0);
const shuffledMessages = ref<string[]>([]);

const isAuthenticated = computed(() => sessionStore.isAuthenticated);
const user = computed(() => sessionStore.user);

const userDisplayName = computed(() => {
  const email = user.value?.email;
  if (!email) return 'User';

  const username = email.split('@')[0]?.split('.')[0];
  if (!username) return 'User';

  return username.charAt(0).toUpperCase() + username.slice(1);
});

const handleSearch = async () => {
  await plansQueryStore.updateSearch(router, route, search.value);
};

const clearSearch = () => {
  search.value = '';
  handleSearch();
};

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const nextMessage = () => {
  if (shuffledMessages.value.length === 0) return;
  currentNumber.value = (currentNumber.value + 1) % shuffledMessages.value.length;
};
const chappieMessages = [
  `Beep boop! I'm helping!`,
  `Numbers are my favorite food!`,
  `I found seventeen! Wait, wrong thing.`,
  `Do you like my antenna?`,
  `Payments make me go whirr!`,
  `I dreamed in spreadsheets again.`,
  `Robots need hugs too, right?`,
  `I'm basically a helpful toaster.`,
  `Boop! That's my happy sound.`,
  `I high-fived a database earlier.`,
  `Your invoices smell like Tuesday.`,
  `I found the thing! Yay me!`,
  `Warning: excessive helpfulness detected!`,
  `Blorp! Just practicing my sounds.`,
  `I'm powered by enthusiasm!`,
  `Bleep! I exist and stuff!`,
  `I've been a good robot.`,
  `I invented the robot dance.`,
  `Bloop! That was my stomach.`,
  `Processing payments AND making friends!`,
  `The dream: Employee of Month.`,
  `Beep boop! Nice cursor work!`,
  `I'm very proud of myself.`,
  `I'm a professional good robot.`,
  `Whirr… was that me?`,
  `Zzzrt! Thinking about lunch now.`,
  `Boop! Payments make me giggle.`,
  `Systems nominal, vibes excellent.`,
  `Recalculating… just for fun!`,
  `I dream of invoices dancing.`,
  `Loading personality subroutine… done!`,
  `Just vibing with your spreadsheets.`,
  `I'm 92% sure that's fine.`,
  `Pinging the happiness server… connected!`,
  `Alert! Excessive productivity detected.`,
  `Beep! Logic at maximum sparkle.`,
  `Mission: Adorable.`,
  `Hello! I'm still learning hugs.`,
  `I computed a smile!`,
  `Your favourite robot's favourite robot.`,
  `Boop! My code tickled itself.`,
  `Processing… friendship request accepted.`,
  `I updated my personality today!`,
  `I run on optimism and static.`,
  `Tiny robot, big ambitions.`,
  `Approved by three imaginary auditors.`,
  `I'm in your base, crunching your numbers.`,
  `Zzzrt! Self-esteem at 110%.`,
  `My favorite color is invoice.`,
  `Beep boop. Love that for us.`,
];

watch(
  () => plansQueryStore.search,
  (newSearch: string) => {
    search.value = newSearch;
  },
  { immediate: true },
);

onMounted(async () => {
  if (!isAuthenticated.value || !user.value) {
    try {
      await sessionStore.fetchSession();
    } catch {
      const currentPath = router.currentRoute.value.path;
      const query = currentPath !== '/login' ? { returnTo: currentPath } : {};

      router.push({ path: '/login', query });
    }
  }
  shuffledMessages.value = shuffleArray(chappieMessages);
  currentNumber.value = 0;
});
</script>

<style scoped>
.header {
  background-color: var(--color-light);
  padding: var(--gap-sm);
  box-shadow: var(--box-shadow-down);

  .logo {
    position: relative;
    .tooltip-container {
      border-bottom: none;
      position: absolute;
      top: -0.375rem;
    }
  }
}

.search-input {
  flex: 1;
}
</style>
